// The reference "now" that document validity (valid / expiring soon / expired)
// is measured against.
//
// For a LIVE loan that's today — a doc whose window has lapsed really is expired.
// For a CLOSED loan, measuring against today is misleading: every dated document
// eventually reads "expired" months after the loan funded, even though it was
// perfectly valid at closing (which is the only moment that mattered). So once
// the loan is closed we freeze the clock at an anchor date and judge the docs
// as of then.
//
// ── The anchor rules (business rule, 2026-09-23) ──────────────────────────
//
// PRE-CLOSE (underwriting pipeline)
//   Closed = `loanStatus` is Closed. Anchor = the note date (`originationDate`)
//   / funding date (`fundedDate`), falling back to the estimated closing date
//   (`estimatedClosingDate` — stamped by `mark_loan_closed` — then the model's
//   `expectedClosingDate`, which the UI labels "Estimated Closing Date").
//   Not closed → today.
//
// ACQUISITIONS (post-close pipeline)
//   Closed = `loanClosed === true` (the correspondent's note is signed).
//   Anchor = `originationDate` only. Not closed → today (each document's own
//   effective date → today, the live countdown).
//
// Either pipeline, closed but no anchor date on file → today, flagged
// `unresolved` so the surface can say the reference is uncertain.
//
// The backend's resolver (`app/services/loan_review/document_expiration.py`)
// orders fundedDate > closingScheduledDate > expectedClosingDate >
// originationDate and isn't pipeline-aware; the FE rule above is deliberately
// the business's, not a mirror of that one.

// Milestone statuses that mean a pre-close loan has closed. `loanStatus` is the
// FE's canonical list (selectOptionsLoanStatus) — "Closed" is what
// `mark_loan_closed` stamps; the "w/ Post Closing Conditions" variant is the
// legacy string still carried across the pipeline columns and the mark-loan
// button, so it stays recognized here.
const CLOSED_LOAN_STATUSES = new Set([
  "Closed",
  "Closed w/ Post Closing Conditions",
]);

// Anchor-date priority per pipeline, highest first.
const PRECLOSE_ANCHOR_FIELDS = [
  "originationDate",
  "fundedDate",
  "estimatedClosingDate",
  "expectedClosingDate",
];
const ACQUISITION_ANCHOR_FIELDS = ["originationDate"];

/**
 * Has this loan closed, for the purpose of judging document validity?
 *
 * @param {object} loan  the loan (loanDrilldown) object
 * @param {{isAcquisition?: boolean}} [opts]  which pipeline the loan is on
 * @returns {boolean}
 */
export const isLoanClosed = (loan, { isAcquisition = false } = {}) =>
  isAcquisition
    ? loan?.loanClosed === true
    : CLOSED_LOAN_STATUSES.has(loan?.loanStatus);

/**
 * The date a closed loan's documents should be judged as of, as a raw field
 * value, plus which field it came from. Returns nulls when nothing resolves.
 * Callers must gate on `isLoanClosed` first — `getValidityReference` does.
 *
 * @param {object} loan
 * @param {{isAcquisition?: boolean}} [opts]
 * @returns {{value: *, field: string|null}}
 */
export const resolveClosingDate = (loan, { isAcquisition = false } = {}) => {
  const fields = isAcquisition
    ? ACQUISITION_ANCHOR_FIELDS
    : PRECLOSE_ANCHOR_FIELDS;
  for (const field of fields) {
    const value = loan?.[field];
    if (!value) continue;
    const ms = new Date(value).getTime();
    if (!Number.isNaN(ms)) return { value, field };
  }
  return { value: null, field: null };
};

// A date-only anchor as epoch ms at LOCAL midnight of its calendar day, so
// `daysUntilDate` (which floors to local midnight) can't slip it a day in a
// negative-offset zone.
const anchorMs = (value) => {
  const datePart = String(value).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    const [y, m, d] = datePart.split("-").map(Number);
    return new Date(y, m - 1, d).getTime();
  }
  return new Date(value).getTime();
};

/**
 * Everything a surface needs to DISCLOSE the frozen clock. Without this a
 * closed loan renders "Valid" / "Expires in 40 days" with no hint that the
 * countdown stopped at closing, which reads as a bug — and post-close these
 * evaluations are informational (what was true at close), not a claim that
 * the document is current today.
 *
 * `frozen` is false when the loan is live, OR when it's closed but no anchor
 * date resolved — in that case we fall back to today, and `unresolved` is true
 * so the surface can say the reference is uncertain rather than silently
 * showing a countdown measured from the wrong day.
 *
 * @param {object} loan
 * @param {{isAcquisition?: boolean}} [opts]
 * @returns {{frozen: boolean, unresolved: boolean, nowMs: number,
 *            closingDate: *, closingField: string|null}}
 */
export const getValidityReference = (loan, opts = {}) => {
  const live = {
    frozen: false,
    unresolved: false,
    nowMs: Date.now(),
    closingDate: null,
    closingField: null,
  };
  if (!isLoanClosed(loan, opts)) return live;
  const { value, field } = resolveClosingDate(loan, opts);
  const ms = value ? anchorMs(value) : NaN;
  if (!value || Number.isNaN(ms)) return { ...live, unresolved: true };
  return {
    frozen: true,
    unresolved: false,
    nowMs: ms,
    closingDate: value,
    closingField: field,
  };
};

// ── Projection: "will it still be valid at closing?" ───────────────────────
//
// A LIVE loan is judged against today, but the question a closer actually asks
// is whether each document will still be good on the day it closes. This is the
// second reference day for that comparison. Pre-close uses the firm scheduled
// closing when one is set, else the estimated closing date; an acquisition that
// hasn't closed uses its estimated closing date. A closed loan has no
// projection — its reference day already IS the close (see above).
const PRECLOSE_PROJECTION_FIELDS = ["closingScheduledDate", "expectedClosingDate"];
const ACQUISITION_PROJECTION_FIELDS = ["expectedClosingDate"];

// Every loan field the two reference days read — spread into a useMemo
// dependency list (`VALIDITY_FIELDS.map((f) => loan?.[f])`) so a caller
// re-evaluates exactly when either answer can change.
export const VALIDITY_FIELDS = [
  "loanStatus",
  "loanClosed",
  ...PRECLOSE_ANCHOR_FIELDS,
  "closingScheduledDate",
];

/**
 * The upcoming closing date a LIVE loan's documents should also be checked
 * against, or null when the loan is closed, no date is set, or the date on
 * file has already passed (a stale estimate says nothing about the future).
 *
 * @param {object} loan
 * @param {{isAcquisition?: boolean}} [opts]
 * @returns {{closingMs: number, closingDate: *, closingField: string}|null}
 */
export const getClosingProjection = (loan, opts = {}) => {
  if (isLoanClosed(loan, opts)) return null;
  const fields = opts.isAcquisition
    ? ACQUISITION_PROJECTION_FIELDS
    : PRECLOSE_PROJECTION_FIELDS;
  const now = new Date();
  const todayMs = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).getTime();
  for (const field of fields) {
    const value = loan?.[field];
    if (!value) continue;
    const ms = anchorMs(value);
    if (Number.isNaN(ms)) continue;
    // A date already behind us is a slipped estimate — try the next one.
    if (ms < todayMs) continue;
    return { closingMs: ms, closingDate: value, closingField: field };
  }
  return null;
};

/**
 * @param {object} loan  the loan (loanDrilldown) object
 * @param {{isAcquisition?: boolean}} [opts]
 * @returns {number} epoch ms to treat as "now" when judging doc validity
 */
export const getValidityReferenceMs = (loan, opts) =>
  getValidityReference(loan, opts).nowMs;

export default getValidityReferenceMs;

// The fields each rule reads, in priority order — for surfaces that have to
// NAME them (a notice listing what to fill in, a slipped date to call out).
export const anchorFieldsFor = ({ isAcquisition = false } = {}) =>
  isAcquisition ? ACQUISITION_ANCHOR_FIELDS : PRECLOSE_ANCHOR_FIELDS;
export const projectionFieldsFor = ({ isAcquisition = false } = {}) =>
  isAcquisition ? ACQUISITION_PROJECTION_FIELDS : PRECLOSE_PROJECTION_FIELDS;

// How a validity date field is named to a user.
export const VALIDITY_DATE_FIELD_LABEL = {
  originationDate: "Note date",
  fundedDate: "Funded date",
  estimatedClosingDate: "Estimated closing date",
  expectedClosingDate: "Expected closing date",
  closingScheduledDate: "Scheduled closing date",
};
